/** @jsxImportSource @emotion/react */

import { EuiButtonIcon, EuiFlexGroup } from "@inscopix/ideas-eui";
import {
  CellEditRequestEvent,
  ColDef,
  ICellRendererParams,
  IsRowMaster,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import assert from "assert";
import { useCallback, useMemo } from "react";
import { useToolEditorContext } from "components/ToolEditor/Provider/ToolEditorProvider";
import { CellEditorBoolean } from "./CellEditors/CellEditorBoolean";
import { CellEditorType } from "./CellEditors/CellEditorType";
import { ParameterEditorDetailRenderer } from "./DetailRenderer/ParameterEditorDetailRenderer";
import {
  DraftSpecParam,
  ParamsEditorRowDatum,
  CellEditRequestValue,
} from "components/ToolEditor/ToolEditor.types";

import ToolPathSingleParamTypeV3Schema from "types/json-schemas/ToolPathSingleParamTypeV3.json";
import ToolIntParamTypeV3Schema from "types/json-schemas/ToolIntParamTypeV3.json";
import ToolStringParamTypeV3Schema from "types/json-schemas/ToolStringParamTypeV3.json";
import ToolFloatParamTypeV3Schema from "types/json-schemas/ToolFloatParamTypeV3.json";
import ToolChoiceParamTypeV3Schema from "types/json-schemas/ToolChoiceParamTypeV3.json";
import ToolBooleanParamTypeV3Schema from "types/json-schemas/ToolBooleanParamTypeV3.json";
import ToolRoiFrameParamTypeV3Schema from "types/json-schemas/ToolRoiFrameParamTypeV3.json";
import ToolCellStatusParamTypeV3Schema from "types/json-schemas/ToolCellStatusParamTypeV3.json";
import { captureException } from "@sentry/react";
import { TOOL_EDITOR_DEFAULT_COL_DEF } from "components/ToolEditor/constants";

export const TOOL_EDITOR_PARAMETER_PROPERTIES = {
  ToolPathParam: {
    friendlyName: "File",
    isDetailRendererEnabled: true,
    isDefaultEditable: "default" in ToolPathSingleParamTypeV3Schema.properties,
  } as const,
  ToolStringParam: {
    friendlyName: "String",
    isDetailRendererEnabled: true,
    isDefaultEditable: "default" in ToolStringParamTypeV3Schema.properties,
  } as const,
  ToolIntRangeParam: {
    friendlyName: "Integer",
    isDetailRendererEnabled: true,
    isDefaultEditable: "default" in ToolIntParamTypeV3Schema.properties,
  } as const,
  ToolFloatRangeParam: {
    friendlyName: "Float",
    isDetailRendererEnabled: true,
    isDefaultEditable: "default" in ToolFloatParamTypeV3Schema.properties,
  } as const,
  ToolChoiceParam: {
    friendlyName: "Choice",
    isDetailRendererEnabled: true,
    isDefaultEditable: "default" in ToolChoiceParamTypeV3Schema.properties,
  } as const,
  ToolBooleanParam: {
    friendlyName: "Boolean",
    isDetailRendererEnabled: true,
    isDefaultEditable: "default" in ToolBooleanParamTypeV3Schema.properties,
  } as const,
  ToolRoiFrameParam: {
    friendlyName: "Roi",
    isDetailRendererEnabled: true,
    isDefaultEditable: "default" in ToolRoiFrameParamTypeV3Schema.properties,
  } as const,
  ToolCellStatusParam: {
    friendlyName: "Cell Status",
    isDetailRendererEnabled: true,
    isDefaultEditable: "default" in ToolCellStatusParamTypeV3Schema.properties,
  } as const,
} as const;

interface ToolSpecEditorColDef extends ColDef<ParamsEditorRowDatum> {
  colId: keyof DraftSpecParam | "default" | undefined;
}

const detailCellRendererParams = { style: { backgroundColor: "black" } };
const css = { width: "100%", minWidth: 800 };
interface ParametersEditorProps {
  rowData: ParamsEditorRowDatum[];
}

export const ParametersEditor = ({ rowData }: ParametersEditorProps) => {
  const updateSpecParamOrder = useToolEditorContext(
    (s) => s.updateSpecParamOrder,
  );
  const updateSpecParamBaseInfo = useToolEditorContext(
    (s) => s.updateSpecParamBaseInfo,
  );
  const updateSpecParam = useToolEditorContext((s) => s.updateSpecParam);
  const isRowMaster: IsRowMaster<ParamsEditorRowDatum> = useCallback((data) => {
    return TOOL_EDITOR_PARAMETER_PROPERTIES[data.type.param_type]
      .isDetailRendererEnabled;
  }, []);

  const columnDefs: ToolSpecEditorColDef[] = useMemo(
    () => [
      {
        colId: "key",
        field: "key",
        cellRenderer: "agGroupCellRenderer",
        width: 150,
      },
      {
        colId: "name",
        field: "name",
        width: 150,
        editable: true,
        cellEditor: "agTextCellEditor",
        valueGetter: ({ data }) => data?.name,
      },
      {
        colId: "type",
        field: "type",
        width: 75,
        cellRenderer: ({ data }: ICellRendererParams<ParamsEditorRowDatum>) => {
          const paramType = data?.type.param_type;
          assert(paramType !== undefined, "Data undefined in cell renderer");
          return TOOL_EDITOR_PARAMETER_PROPERTIES[paramType].friendlyName;
        },
        cellEditor: CellEditorType,
        editable: true,
      },
      {
        colId: "required",
        field: "required",
        width: 80,
        cellEditor: "agCheckboxCellEditor",
        editable: true,
      },
      {
        colId: "default",
        headerName: "default",
        width: 75,
        valueGetter: ({ data }) => {
          if (data === undefined) {
            return null;
          }
          const type = data.type;

          return "default" in type ? type.default : null;
        },
        editable: ({ data }) => {
          const type = data?.type.param_type;
          if (type === undefined) {
            return false;
          }
          return TOOL_EDITOR_PARAMETER_PROPERTIES[type].isDefaultEditable;
        },
        cellEditorSelector: (params) => {
          if (
            !TOOL_EDITOR_PARAMETER_PROPERTIES[params.data.type.param_type]
              .isDefaultEditable
          ) {
            return undefined;
          }

          switch (params.data.type.param_type) {
            case "ToolStringParam": {
              return { component: "agTextCellEditor" };
            }
            case "ToolBooleanParam": {
              return {
                component: CellEditorBoolean,
              };
            }
            case "ToolIntRangeParam": {
              return {
                component: "agNumberCellEditor",
                params: {
                  precision: 0,
                },
              };
            }
            case "ToolFloatRangeParam": {
              return { component: "agNumberCellEditor" };
            }
            case "ToolChoiceParam":
              return { component: "agTextCellEditor" };
            case "ToolPathParam":
            case "ToolRoiFrameParam":
            case "ToolCellStatusParam":
              return undefined;
          }
        },
      },
      {
        colId: "description",
        field: "description",
        headerName: "Description",
        cellEditor: "agTextCellEditor",
        editable: true,
        flex: 1,
      },
      {
        colId: "group",
        headerName: "Group",
        width: 200,
        editable: true,
        cellEditor: "agTextCellEditor",
        valueGetter: ({ data }) => data?.group,
      },
      {
        colId: undefined,
        headerName: "Order",
        width: 60,
        cellRenderer: ({ data }: ICellRendererParams<ParamsEditorRowDatum>) => {
          if (data === undefined) {
            return null;
          }

          const paramKey = data.key;

          return (
            <EuiFlexGroup gutterSize="none" style={{ marginLeft: -7 }}>
              <EuiButtonIcon
                key={`${paramKey}-up`}
                iconSize="s"
                aria-label={`Move parameter ${data?.key ?? ""} up`}
                iconType="arrowUp"
                onClick={() => updateSpecParamOrder(paramKey, "UP")}
              />
              <EuiButtonIcon
                key={`${paramKey}-down`}
                iconSize="s"
                aria-label={`Move parameter ${data?.key ?? ""} down`}
                iconType="arrowDown"
                onClick={() => updateSpecParamOrder(paramKey, "DOWN")}
              />
            </EuiFlexGroup>
          );
        },
      },
    ],
    [updateSpecParamOrder],
  );

  const getRowId = useCallback(
    ({ data }: { data: (typeof rowData)[number] }) => data.key,
    [],
  );

  const handleCellEditRequest = useCallback(
    (e: CellEditRequestEvent<ParamsEditorRowDatum>) => {
      const newValue = e.newValue as CellEditRequestValue;
      const paramKey = e.data.key;
      const colDef = e.colDef as ToolSpecEditorColDef;
      const type = e.data.type.param_type;
      const attrKey = colDef.colId;

      if (attrKey === undefined) {
        return;
      }

      switch (attrKey) {
        case "default": {
          if (
            TOOL_EDITOR_PARAMETER_PROPERTIES[type].isDefaultEditable === false
          ) {
            return;
          }
          // ag grid cell editors can return empty string or null when deleting values
          const sanitizedValue = (() => {
            switch (type) {
              case "ToolStringParam":
                if (
                  newValue === "" ||
                  newValue === null ||
                  newValue === undefined ||
                  typeof newValue == "boolean"
                ) {
                  return undefined;
                }
                return String(newValue);
              case "ToolIntRangeParam": {
                const num = parseInt(String(newValue));
                if (Number.isNaN(num)) {
                  return undefined;
                }
                return num;
              }
              case "ToolFloatRangeParam": {
                const num = parseInt(String(newValue));
                if (Number.isNaN(num)) {
                  return undefined;
                }
                return num;
              }
              case "ToolBooleanParam": {
                if (
                  newValue === "" ||
                  newValue === null ||
                  newValue === undefined
                ) {
                  return undefined;
                }
                return Boolean(newValue);
              }
              case "ToolChoiceParam":
                return String(newValue);
              case "ToolPathParam":
              case "ToolRoiFrameParam":
              case "ToolCellStatusParam":
                return undefined;
            }
          })();

          return updateSpecParam(
            paramKey,
            type,
            { [attrKey]: sanitizedValue },
            true,
          );
        }

        case "type": // type editable through custom cell editor
        case "key": // key not editable
          return captureException(`Unexpected cell edit for ${attrKey}`);

        case "group":
        case "description":
        case "name": {
          if (typeof newValue === "string") {
            updateSpecParamBaseInfo(paramKey, {
              [attrKey]: newValue !== "" ? newValue : undefined,
            });
          } else {
            updateSpecParamBaseInfo(paramKey, {
              [attrKey]: undefined,
            });
          }
          break;
        }
        case "required": {
          updateSpecParamBaseInfo(paramKey, {
            [attrKey]: Boolean(newValue),
          });
        }
      }

      if (attrKey !== undefined) {
        return updateSpecParamBaseInfo(paramKey, {
          [attrKey]: newValue,
        });
      }
    },
    [updateSpecParam, updateSpecParamBaseInfo],
  );

  return (
    <AgGridReact<ParamsEditorRowDatum>
      className="ag-theme-balham-cell-borders"
      css={css}
      domLayout="autoHeight"
      columnDefs={columnDefs}
      defaultColDef={TOOL_EDITOR_DEFAULT_COL_DEF}
      rowData={rowData}
      getRowId={getRowId}
      detailCellRenderer={ParameterEditorDetailRenderer}
      isRowMaster={isRowMaster}
      masterDetail={true}
      detailCellRendererParams={detailCellRendererParams}
      detailRowAutoHeight
      reactiveCustomComponents
      readOnlyEdit
      onCellEditRequest={handleCellEditRequest}
    />
  );
};
